import { useState } from 'react';
import type { DataTableView } from '@/shared/ui/data-table/types';

/** Which saved view is showing, and how to change it. */
export type UseSavedViewsState = {
  activeViewId: string | undefined;
  applyView: (id: string) => void;
  /**
   * The filters the active view narrows by, or `undefined` when it narrows by nothing.
   *
   * A view's filters are a CLAIM about the collection — "Draft" means the drafts, not
   * the drafts that happen to be on this page. The caller merges these into the query
   * it sends, so the claim is true of the data rather than only of the tab's label.
   */
  activeViewFilters: Record<string, string> | undefined;
};

/**
 * The active saved view.
 *
 * The hook reports the active view's filters rather than applying them itself: the
 * query is the caller's to build, and a hook that silently rewrote it would make the
 * page's data path impossible to follow.
 *
 * It also does NOT reset the page. The caller owns the query, so the caller is the
 * only one that can see the filters change and return to page one — and it must,
 * because page 7 of a newly filtered collection is not page 7 of the previous one.
 */
export function useSavedViews(views: DataTableView[] | undefined): UseSavedViewsState {
  const [activeViewId, setActiveViewId] = useState(views?.[0]?.id);
  const activeView = views?.find((candidate) => candidate.id === activeViewId);

  return {
    activeViewId,
    activeViewFilters: activeView?.filters,
    applyView: setActiveViewId,
  };
}